import { Loader2, RefreshCw, Sparkles, X } from "lucide-react";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "sonner";

import type { AssistantVisualUniverseRegenerationPreviewReference } from "@/lib/assistant-visual-universe-regeneration-previews";
import { trpc } from "@/lib/trpc";
import { Badge, Button } from "@runcheetah/ui";

type ProposalState = "active" | "cancelled" | "queued" | "generating" | "completed";

export function AssistantVisualUniverseRegenerationPreviews({
  brandId,
  references,
  isStale = false,
  visualUniverseHref,
}: {
  brandId: string;
  references: AssistantVisualUniverseRegenerationPreviewReference[];
  isStale?: boolean;
  visualUniverseHref: string;
}) {
  if (references.length === 0) {
    return null;
  }

  return (
    <div className="w-full space-y-3">
      {references.map((reference) => (
        <AssistantVisualUniverseRegenerationPreviewBanner
          key={reference.id}
          brandId={brandId}
          reference={reference}
          isStale={isStale}
          visualUniverseHref={visualUniverseHref}
        />
      ))}
    </div>
  );
}

function AssistantVisualUniverseRegenerationPreviewBanner({
  brandId,
  reference,
  isStale,
  visualUniverseHref,
}: {
  brandId: string;
  reference: AssistantVisualUniverseRegenerationPreviewReference;
  isStale: boolean;
  visualUniverseHref: string;
}) {
  const intl = useIntl();
  const utils = trpc.useUtils();
  const proposalStorageKey = getProposalStorageKey(brandId, reference.id);
  const [proposalState, setProposalState] = useState<ProposalState>(() =>
    loadProposalState(proposalStorageKey),
  );
  const [error, setError] = useState<string | null>(null);
  const closed = proposalState === "active" && isStale;
  const cancelled = proposalState === "cancelled";
  const completed = proposalState === "completed";
  const isTrackingGeneration = proposalState === "queued" || proposalState === "generating";
  const showGenerationStatus = isTrackingGeneration || completed;
  const visualUniverseQuery = trpc.brand.getVisualUniverse.useQuery(
    { brandId },
    {
      enabled: isTrackingGeneration,
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchInterval: (query) => {
        const generationState = query.state.data?.generationStatus.state;
        return isTrackingGeneration ||
          generationState === "queued" ||
          generationState === "processing"
          ? 5_000
          : false;
      },
    },
  );
  const observedGenerationState = visualUniverseQuery.data?.generationStatus.state;
  const generateVisualUniverse = trpc.brand.generateVisualUniverse.useMutation({
    onSuccess: async () => {
      saveProposalState(proposalStorageKey, "queued");
      setProposalState("queued");
      setError(null);
      await utils.brand.getVisualUniverse.invalidate({ brandId });
      toast.success(
        intl.formatMessage({
          id: "assistantVisualUniverseRegeneration.queueSuccess",
          defaultMessage: "Visual universe regeneration queued",
        }),
      );
    },
    onError: (mutationError) => setError(mutationError.message),
  });

  const confirm = () => {
    if (closed || showGenerationStatus || cancelled || generateVisualUniverse.isPending) {
      return;
    }

    generateVisualUniverse.mutate({
      brandId,
      referenceGeneratedAssetId: reference.sourceGeneratedAssetId,
      ...(reference.direction ? { referenceInstruction: reference.direction } : {}),
    });
  };

  useEffect(() => {
    if (!isTrackingGeneration || visualUniverseQuery.isFetching || !observedGenerationState) {
      return;
    }

    const nextState: ProposalState =
      observedGenerationState === "processing"
        ? "generating"
        : observedGenerationState === "queued"
          ? "queued"
          : "completed";

    if (nextState === proposalState) {
      return;
    }

    saveProposalState(proposalStorageKey, nextState);
    setProposalState(nextState);
  }, [
    isTrackingGeneration,
    observedGenerationState,
    proposalState,
    proposalStorageKey,
    visualUniverseQuery.isFetching,
  ]);

  if (cancelled) {
    return (
      <div className="w-full rounded-3xl border border-border/70 bg-muted/35 px-4 py-3 text-sm text-muted-foreground">
        <FormattedMessage
          id="assistantVisualUniverseRegeneration.cancelled"
          defaultMessage="Visual universe regeneration proposal cancelled."
        />
      </div>
    );
  }

  return (
    <article className="w-full overflow-hidden border-y border-border/60 bg-transparent">
      <div className="grid gap-4 p-4 sm:grid-cols-[9.5rem_1fr]">
        <div className="relative mx-auto aspect-square w-full max-w-48 overflow-hidden rounded-2xl bg-muted shadow-sm sm:mx-0 sm:max-w-none">
          <img
            src={reference.signedUrl}
            alt={intl.formatMessage({
              id: "assistantVisualUniverseRegeneration.selectedImageAlt",
              defaultMessage: "Selected generated image",
            })}
            className="h-full w-full object-cover"
            loading="lazy"
          />
          <div className="absolute left-2 top-2 rounded-full bg-background/86 px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none tracking-[0.1em] text-foreground shadow-sm backdrop-blur">
            <FormattedMessage
              id="assistantVisualUniverseRegeneration.seedBadge"
              defaultMessage="Seed"
            />
          </div>
        </div>

        <div className="min-w-0 space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground/88">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center text-muted-foreground">
                  <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
                <FormattedMessage
                  id="assistantVisualUniverseRegeneration.title"
                  defaultMessage="Regenerate the visual universe from this image?"
                />
              </div>
              <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
                {reference.summary ?? (
                  <FormattedMessage
                    id="assistantVisualUniverseRegeneration.description"
                    defaultMessage="Cheetah will use the selected generated image as creative-direction evidence, then rebuild the brand visual universe and queue fresh image studies."
                  />
                )}
              </p>
            </div>
            <Badge
              variant="outline"
              className="rounded-md border-border/70 bg-card/70 text-muted-foreground"
            >
              <FormattedMessage
                id="assistantVisualUniverseRegeneration.badge"
                defaultMessage="Brand kit"
              />
            </Badge>
          </div>

          {reference.direction ? (
            <div className="space-y-2 rounded-2xl bg-background/42 px-3 py-3 text-sm leading-6">
              <p>
                <span className="font-medium text-foreground">
                  <FormattedMessage
                    id="assistantVisualUniverseRegeneration.directionLabel"
                    defaultMessage="Direction"
                  />
                  :
                </span>{" "}
                <span className="text-muted-foreground">{reference.direction}</span>
              </p>
            </div>
          ) : null}

          {closed ? (
            <div className="border-t border-border/60 pt-3 text-sm font-medium text-foreground/58">
              <FormattedMessage
                id="assistantVisualUniverseRegeneration.superseded"
                defaultMessage="This proposal can no longer be accepted because the conversation has moved forward."
              />
            </div>
          ) : showGenerationStatus ? (
            <div className="flex flex-col gap-3 border-t border-border/60 pt-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
              <span className="inline-flex items-center gap-1.5 font-medium text-foreground/72">
                {completed ? (
                  <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
                ) : (
                  <RefreshCw className="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
                )}
                {completed ? (
                  <>
                    <FormattedMessage
                      id="assistantVisualUniverseRegeneration.completedLabel"
                      defaultMessage="Updated"
                    />
                    :
                  </>
                ) : (
                  <>
                    <FormattedMessage
                      id="assistantVisualUniverseRegeneration.generatingLabel"
                      defaultMessage="Generating"
                    />
                    :
                  </>
                )}
                <span className="font-normal text-muted-foreground">
                  {completed ? (
                    <FormattedMessage
                      id="assistantVisualUniverseRegeneration.completed"
                      defaultMessage="Visual universe regenerated."
                    />
                  ) : (
                    <FormattedMessage
                      id="assistantVisualUniverseRegeneration.generating"
                      defaultMessage="Visual universe is being generated."
                    />
                  )}
                </span>
              </span>
              <a
                href={visualUniverseHref}
                className="inline-flex h-9 items-center justify-center rounded-full bg-primary px-4 text-xs font-bold text-primary-foreground transition hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45"
              >
                <FormattedMessage
                  id="assistantVisualUniverseRegeneration.openBoard"
                  defaultMessage="Open board"
                />
              </a>
            </div>
          ) : (
            <div className="space-y-3 border-t border-border/60 pt-3">
              {error ? (
                <div className="rounded-2xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {error}
                </div>
              ) : null}
              <div className="flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  className="rounded-full text-foreground/65"
                  disabled={generateVisualUniverse.isPending}
                  onClick={() => {
                    saveProposalState(proposalStorageKey, "cancelled");
                    setProposalState("cancelled");
                  }}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                  <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                </Button>
                <Button
                  type="button"
                  className="rounded-full"
                  disabled={generateVisualUniverse.isPending}
                  onClick={confirm}
                >
                  {generateVisualUniverse.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <RefreshCw className="h-4 w-4" aria-hidden="true" />
                  )}
                  <FormattedMessage
                    id="assistantVisualUniverseRegeneration.confirm"
                    defaultMessage="Regenerate board"
                  />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function getProposalStorageKey(brandId: string, proposalId: string) {
  return `assistant-visual-universe-regeneration:${brandId}:${proposalId}`;
}

function loadProposalState(key: string): ProposalState {
  if (typeof window === "undefined") {
    return "active";
  }

  try {
    const raw = window.localStorage.getItem(key);
    if (
      raw === "active" ||
      raw === "cancelled" ||
      raw === "queued" ||
      raw === "generating" ||
      raw === "completed"
    ) {
      return raw;
    }
  } catch {
    return "active";
  }

  return "active";
}

function saveProposalState(key: string, state: ProposalState) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(key, state);
  } catch {
    // Losing persisted proposal state should not block regeneration.
  }
}
